import { useMemo } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import { useI18n } from "@/contexts/I18nContext";

export default function GrowthSimulator({ sector }) {
  const { t, formatPrice, currency } = useI18n();

  const data = useMemo(() => {
    if (!sector) return [];
    const months = t("months");
    return months.map((m, i) => {
      const baseline = sector.monthly_revenue_base_php * Math.pow(1.04, i); // organic 4% growth
      const withDncm = sector.monthly_revenue_base_php * Math.pow(1 + sector.growth_rate, i);
      return {
        name: m,
        baseline: Math.round(baseline),
        withDncm: Math.round(withDncm),
      };
    });
  }, [sector, t]);

  if (!sector) return null;

  return (
    <div data-testid="growth-simulator" className="dncm-card rounded-2xl p-5 sm:p-6">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="dncm-pulse-dot" />
            <span className="font-mono-d text-[11px] uppercase tracking-[0.32em] text-white/65">
              {t("growth_title")}
            </span>
          </div>
          <h4 className="font-display text-xl sm:text-2xl text-white">{sector.name}</h4>
          <p className="text-white/55 text-sm mt-1 max-w-md">{t("growth_subtitle")}</p>
        </div>
        <div className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/55">
          {t("revenue")} · {currency.code}
        </div>
      </div>

      <div style={{ width: "100%", height: 320 }}>
        <ResponsiveContainer>
          <AreaChart data={data} margin={{ top: 8, right: 12, left: 8, bottom: 0 }}>
            <defs>
              <linearGradient id="grad-dncm" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#deff9a" stopOpacity={0.7} />
                <stop offset="100%" stopColor="#deff9a" stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="grad-baseline" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00cfff" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#00cfff" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis
              dataKey="name"
              stroke="rgba(255,255,255,0.4)"
              tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
              tickLine={false}
              axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
            />
            <YAxis
              stroke="rgba(255,255,255,0.4)"
              tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
              tickFormatter={(v) => formatPrice(v, { withSymbol: false, fractionDigits: 0 })}
              tickLine={false}
              axisLine={false}
              width={70}
            />
            <Tooltip
              cursor={{ stroke: "rgba(255,255,255,0.2)" }}
              contentStyle={{
                background: "#0a0d10",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 8,
                color: "white",
              }}
              formatter={(value, key) => {
                const label = key === "withDncm" ? "DNCM Vision" : "Baseline";
                return [formatPrice(value), label];
              }}
              labelStyle={{ color: "rgba(255,255,255,0.55)", fontSize: 11 }}
            />
            <Legend
              wrapperStyle={{ fontSize: 11, color: "rgba(255,255,255,0.65)", fontFamily: "JetBrains Mono", textTransform: "uppercase", letterSpacing: 2 }}
              iconSize={10}
            />
            <Area
              type="monotone"
              name="Baseline"
              dataKey="baseline"
              stroke="#00cfff"
              strokeWidth={1.5}
              fill="url(#grad-baseline)"
              animationDuration={600}
            />
            <Area
              type="monotone"
              name="DNCM Vision"
              dataKey="withDncm"
              stroke="#deff9a"
              strokeWidth={2.5}
              fill="url(#grad-dncm)"
              animationDuration={800}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
